import { AxiosHeaders, isAxiosError } from "axios";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { reportAuthEvent } from "./auth-diagnostics";
import type { TokenBroker } from "./token-broker";

type AuthRequestConfig = InternalAxiosRequestConfig & {
  _authRetried?: boolean;
};

type Broker = Pick<TokenBroker, "getToken" | "reportRejectedSession">;

// Present when a Conditional Access / CAE policy rejects a token that is otherwise valid.
export function parseClaimsChallenge(
  header: string | undefined
): string | undefined {
  if (!header || !/error="insufficient_claims"/.test(header)) return undefined;

  const encoded = /claims="([^"]+)"/.exec(header)?.[1];
  if (!encoded) return undefined;

  try {
    return atob(encoded);
  } catch {
    return undefined;
  }
}

export function attachAuthInterceptors(
  axios: AxiosInstance,
  broker: Broker
): () => void {
  const requestId = axios.interceptors.request.use(async (config) => {
    if ((config as AuthRequestConfig)._authRetried) return config;

    config.headers.Authorization = `Bearer ${await broker.getToken()}`;
    return config;
  });

  const responseId = axios.interceptors.response.use(
    undefined,
    async (error) => {
      const config = isAxiosError(error)
        ? (error.config as AuthRequestConfig | undefined)
        : undefined;
      if (!isAxiosError(error) || error.response?.status !== 401 || !config)
        throw error;

      if (config._authRetried) {
        broker.reportRejectedSession(error);
        throw error;
      }

      const challenge = AxiosHeaders.from(
        error.response.headers as AxiosHeaders
      ).get("www-authenticate");
      let token: string;
      try {
        token = await broker.getToken({
          forceRefresh: true,
          claims: parseClaimsChallenge(
            typeof challenge === "string" ? challenge : undefined
          ),
        });
      } catch {
        throw error;
      }

      reportAuthEvent({
        type: "request-retry",
        decision: "401-forced-refresh",
      });
      config._authRetried = true;
      config.headers.Authorization = `Bearer ${token}`;
      return axios.request(config);
    }
  );

  return () => {
    axios.interceptors.request.eject(requestId);
    axios.interceptors.response.eject(responseId);
  };
}
